import { DatePipe } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, NgZone, Output, ViewChild } from '@angular/core';
import { PendoService } from 'analytics-lib';
import { TemplateUsage, SectionType, Workflow } from 'enums-lib';
import { IEntityMetadata, IIconService, INamedEntity, ISharedServiceProviderService } from 'interfaces-lib';
import { TemplateWorkflowConfig } from 'interfaces-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { ErrorSeverity, MessageNotificationService } from 'message-notification-service-lib';
import { TemplateLaunchIntentService } from 'post-login-action-service-lib';
import { finalize } from 'rxjs/operators';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';
import { IdentityStorage } from 'storage-service-lib';
import { runtimeEnvironment } from '../../environments/environment';
import { SimpleTableComponent } from '../common-components/simple-table/simple-table.component';
import { ISimpleTableRow } from '../common-components/simple-table/simple-table.interfaces';
import { IPendoAwesomeDocDestination, IPendoAwesomeDocTrackEvent } from '../interfaces/ipendo';
import { TemplateLaunchOverlayService } from '../workflow-in-progress/template-launch-overlay.service';
import { TemplateListViewService } from './template-list-view.service';
import { WorkflowCoordinationService } from './workflow-coordination/workflowcoordination.service';

@Component({
  selector: 'app-template-list-view',
  templateUrl: './template-list-view.component.html',
  styleUrls: ['./template-list-view.scss', './web-app.template-list-view.scss', './chrome-extension.template-list-view.scss'],
  standalone: false,
})
export class TemplateListViewComponent {
  @Input() templatesListOpen = false;

  @Output() emitTemplatesListOpen = new EventEmitter<boolean>();

  @ViewChild(SimpleTableComponent) simpleTable?: SimpleTableComponent;

  @ViewChild('searchInputEl') searchInputEl?: ElementRef<HTMLInputElement>;

  datasource: string;
  item: TemplateWorkflowConfig;

  renderMode = runtimeEnvironment.renderMode;
  showSpinner = true;
  templates: TemplateWorkflowConfig[] = [];
  templateRows: ISimpleTableRow[] = [];
  templateSearchFn = (searchText: string, row: TemplateWorkflowConfig): boolean => row.name.toLowerCase().includes(searchText.toLowerCase());
  webAppColumnHeadings = [
    {
      title: 'Type',
      width: '3%',
      sortFunction: TemplateListViewComponent.sortByType,
    },
    {
      title: 'Name',
      width: '39%',
      sortFunction: TemplateListViewComponent.sortByName,
    },
    {
      title: 'Last Modified',
      width: '33%',
      sortFunction: TemplateListViewComponent.sortByLastModified,
    },
    { title: 'Actions', width: '25%' },
  ];
  chromeExtensionColumnHeadings = [
    {
      title: 'Type',
      width: '5%',
      sortFunction: TemplateListViewComponent.sortByType,
    },
    {
      title: 'Name',
      width: '62%',
      sortFunction: TemplateListViewComponent.sortByName,
    },
    { title: '', width: '33%' },
  ];

  columnHeadings = this.renderMode === 'web-app' ? this.webAppColumnHeadings : this.chromeExtensionColumnHeadings;
  entity: INamedEntity | null;
  entitiesMeta: IEntityMetadata[];
  awesomeDocButtonText = this.renderMode === 'web-app' ? 'Create AwesomeDoc' : 'Create';
  entityLabel: string;

  private serviceProvider: ISharedServiceProviderService;
  private adAction = {
    icon: '',
    title: this.awesomeDocButtonText,
    meta: 'action-create-awesomedoc',
  };
  private readonly updateUi: (update) => void;
  private readonly iconService: IIconService;
  private readonly namespaceRecordName;

  constructor(
    private sharedServiceProviderServiceFactory: SharedServiceProviderServiceFactory,
    private identityStorage: IdentityStorage,
    private templateService: TemplateListViewService,
    private messageNotificationService: MessageNotificationService,
    private workflowCoordinationService: WorkflowCoordinationService,
    private pendoService: PendoService,
    private ngZone: NgZone,
    private datePipe: DatePipe,
    private templateLaunchIntentService: TemplateLaunchIntentService,
    private featureFlagService: FeatureFlagService,
    private templateLaunchOverlayService: TemplateLaunchOverlayService,
  ) {
    this.serviceProvider = this.sharedServiceProviderServiceFactory.getServiceProviderService(this.identityStorage.get()?.namespace);
    this.updateUi = runtimeEnvironment.runningAsChromeExtension ? this.updateUiInZone : this.updateUiNoZone;
    this.iconService = this.sharedServiceProviderServiceFactory
      .getServiceProviderService(runtimeEnvironment.origin.namespace)
      .getIconService();

    this.sharedServiceProviderServiceFactory
      .getServiceProviderService(runtimeEnvironment.origin.namespace)
      .getNamedEntityService()
      .namedEntitySelected$.subscribe((entity) => this.entitySelected(entity));
    this.serviceProvider
      .getEntityMetadataService()
      .getEntitiesMetadata()
      .subscribe((entities) => {
        this.entitiesMeta = entities;
        if (this.templates?.length) {
          this.buildTemplateRows();
        }
      });

    this.namespaceRecordName = this.serviceProvider.getTerminologyService().recordName;
  }

  refresh($event): void {
    $event.stopPropagation();
    this.loadTemplates();
  }

  itemActionClick(itemAction: { item: TemplateWorkflowConfig; action: string }): void {
    if (itemAction.action === 'action-create-awesomedoc') {
      this.item = itemAction.item;
      this.datasource = itemAction.item.clientNamespace;
      this.rowClick(itemAction.item);
      this.buildPendoTrackEvent(itemAction.item);
    }
  }

  rowClick(template: TemplateWorkflowConfig): void {
    this.workflowCoordinationService.start(this.entity, template);
  }

  openTemplateList(): void {
    if (!this.templatesListOpen) {
      this.templatesListOpen = true;
      this.emitTemplatesListOpen.emit(this.templatesListOpen);
    }
  }

  closeTemplateList($event): void {
    $event.stopPropagation();
    this.templatesListOpen = !this.templatesListOpen;
    this.emitTemplatesListOpen.emit(this.templatesListOpen);
  }

  private getIcon(type: string) {
    return this.iconService.getIcon(type);
  }

  private updateUiNoZone(update: () => void): void {
    update();
  }

  private updateUiInZone(update: () => void): void {
    this.ngZone.run(() => update());
  }

  private static sortByType(a: TemplateWorkflowConfig, b: TemplateWorkflowConfig): number {
    return a.entity.localeCompare(b.entity);
  }

  private static sortByName(a: TemplateWorkflowConfig, b: TemplateWorkflowConfig): number {
    return a.name.localeCompare(b.name);
  }

  private static sortByLastModified(a: TemplateWorkflowConfig, b: TemplateWorkflowConfig): number {
    return a.dateModified - b.dateModified;
  }

  private buildPendoTrackEvent(item: TemplateWorkflowConfig) {
    const pendoEvent = this.filterAwesomeDocDataForPendo(item);
    this.pendoService?.recordEvent('Create AwesomeDoc', pendoEvent);
  }

  private filterAwesomeDocDataForPendo(item: TemplateWorkflowConfig): IPendoAwesomeDocTrackEvent {
    const destinations: IPendoAwesomeDocDestination[] =
      item.actionConfigs
        ?.filter((cfg: any) => cfg.type === 'documentDestination')
        .map((dest: any) => ({
          entityType: dest.properties?.entityType || '',
          paths: dest.properties?.paths || [],
        })) || [];

    const docxTemplate = item.sections?.find((s: any) => s.type === SectionType.DocxTemplate)?.sourceValue || '';

    const htmlTemplate = item.sections?.find((s: any) => s.type === SectionType.HtmlDocxTemplate)?.sourceValue || '';

    return {
      entity: item.entity || '',
      workflowType: item.workflow || '',
      templateName: docxTemplate as string,
      htmlTemplateName: htmlTemplate as string,
      destinations,
      templateCreatedBy: item.createdBy || '',
      awesomeDocName: item.name || '',
      isPublic: item.isPublic ?? false,
    };
  }

  // Keyboard Shortcuts
  // - Use Alt + Shift + A to toggle the (AwesomeDocs) Templates List
  // - Use Ctrl + Shift + S to focus the Templates search box
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (event.altKey && event.shiftKey && event.code === 'KeyA') {
      event.preventDefault();
      this.templatesListOpen = !this.templatesListOpen;
      this.emitTemplatesListOpen.emit(this.templatesListOpen);
      this.pendoService?.recordEvent('Alt+Shift+A pressed - to toggle the AwesomeDocs Templates List');
    } else if (event.ctrlKey && event.shiftKey && event.code === 'KeyS') {
      event.preventDefault();
      this.focusTemplateSearch();
      this.pendoService?.recordEvent('Ctrl+Shift+S pressed - to focus the Templates search box');
    }
  }

  onSearchInput(event: Event): void {
    this.simpleTable?.search((event.target as HTMLInputElement).value);
  }

  clearSearch(): void {
    this.resetSearch();
    this.searchInputEl?.nativeElement?.focus();
  }

  private focusTemplateSearch(): void {
    if (!this.templatesListOpen) {
      this.openTemplateList();
      setTimeout(() => this.searchInputEl?.nativeElement?.focus());
      return;
    }
    this.searchInputEl?.nativeElement?.focus();
  }

  private resetSearch(): void {
    if (this.searchInputEl?.nativeElement) {
      this.searchInputEl.nativeElement.value = '';
    }
    this.simpleTable?.search('');
  }

  private loadTemplates(): void {
    this.showSpinner = true;
    this.resetSearch();
    this.templateService
      .getTemplates(runtimeEnvironment.origin.namespace, this.entity.type, this.entity.id)
      .pipe(finalize(() => this.ngZone.run(() => (this.showSpinner = false))))
      .subscribe(
        (fetchedTemplates) => {
          this.templates = fetchedTemplates
            .filter((template) => TemplateListViewComponent.isAwesomeDocsTemplate(template))
            .sort(TemplateListViewComponent.sortByName);
          this.buildTemplateRows();
          this.launchRequestedTemplateWithFlag();
        },
        (error) => {
          this.templateLaunchOverlayService.hide();
          this.messageNotificationService.error('Failed to load templates.', error);
        },
      );
  }

  private static isAwesomeDocsTemplate(template: TemplateWorkflowConfig): boolean {
    return (
      template.isPublic &&
      template.workflow === Workflow.SimpleDocx &&
      (template.usageTypes == null || template.usageTypes.includes(TemplateUsage.AwesomeDocs))
    );
  }

  // to be replaced by launchRequestedTemplate on flag removal
  private launchRequestedTemplateWithFlag() {
    this.featureFlagService.ready().subscribe(() => {
      if (!this.featureFlagService.enabled(FeatureFlagService.FLAGS.templateDirectUrl)()) {
        return;
      }

      this.launchRequestedTemplate();
    })
  }

  private launchRequestedTemplate(): void {
    const requestedTemplateId = this.templateLaunchIntentService.consume();
    if (!requestedTemplateId || !this.entity) {
      return;
    }
    this.selectRequestedTemplate(requestedTemplateId);
  }

  private selectRequestedTemplate(requestedTemplateId: string): void {
    const matches = (template: TemplateWorkflowConfig) => String(template.id) === requestedTemplateId;

    const requestedTemplate = this.templates.find(matches);
    if (requestedTemplate) {
      this.templateLaunchOverlayService.stage('Preparing');
      this.rowClick(requestedTemplate);
      return;
    }

    this.templateLaunchOverlayService.hide();
    this.messageNotificationService.error(
      'The template in this link is not available for this record. It may have been deleted, unpublished, ' +
        'or it may not apply to this record type.',
      undefined,
      undefined,
      ErrorSeverity.CRITICAL,
    );
  }

  private buildTemplateRows(): void {
    const actions = [this.adAction];
    this.ngZone.run(() => {
      this.templateRows = this.templates
        .map((t) => {
          const entityMeta = this.findEntityMeta(t.entity);
          const iconCell: { icon: string; tooltip: string; iconUrl?: string; iconColor?: string } = {
            icon: this.getIcon(t.entity),
            tooltip: this.adIconTooltip(t.entity),
          };
          if (entityMeta?.iconUrl?.trim()) {
            iconCell.iconUrl = entityMeta.iconUrl.trim();
          }
          if (entityMeta?.iconColor?.trim()) {
            iconCell.iconColor = entityMeta.iconColor.trim();
          }

          return {
            cells:
              this.renderMode === 'web-app'
                ? [
                    iconCell,
                    { text: t.name },
                    {
                      text: this.datePipe.transform(t.dateModified * 1000, 'medium'),
                    },
                    { actions: actions },
                  ]
                : [
                    iconCell,
                    { text: t.name },
                    { actions: actions },
                  ],
            data: t,
            rowTooltipText: 'Create AwesomeDoc',
          };
        });
    });
  }

  private adIconTooltip(entity: string): string {
    const entityLabel = this.findEntityMeta(entity)?.label;
    return 'AwesomeDoc for ' + entityLabel + ' ' + this.namespaceRecordName;
  }

  private entitySelected(entity: INamedEntity | null): void {
    if (!entity) {
      this.updateUi(() => {
        this.entity = undefined;
      });
      return;
    }

    this.entity = entity;
    this.entityLabel = this.findEntityMeta(entity.type)?.label;
    this.loadTemplates();
  }

  private findEntityMeta(entityType: string): IEntityMetadata | undefined {
    return this.entitiesMeta?.find((entity) => entity.name.toLowerCase() === entityType.toLowerCase());
  }
}
